import React from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';

// Cinemagraph-style image wrapper: full image visible (object-contain) with a
// blurred version of the SAME image used as a soft backdrop, so square/portrait
// images look natural in 16:9 or 16:10 slots without cropping the subject.
export const SmartImage = ({ src, alt, hover = true }) => (
  <div className="absolute inset-0 overflow-hidden">
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-black/10" />
    <img
      src={src}
      alt={alt}
      className={`relative w-full h-full object-contain ${hover ? 'group-hover:scale-[1.03] transition-transform duration-500' : ''}`}
      loading="lazy"
    />
  </div>
);

export const CategoryBadge = ({ category, className = '' }) => (
  <span className={`inline-block bg-[#8b2c3f] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 ${className}`}>
    {category}
  </span>
);

export const MetaLine = ({ author, date, readTime }) => (
  <div className="flex items-center flex-wrap gap-2 text-[12px] text-gray-500">
    {author && (
      <>
        <span>Par <a href="#" className="text-gray-700 hover:text-[#8b2c3f] transition-colors">{author}</a></span>
        <span className="text-gray-300">|</span>
      </>
    )}
    {date && <span>{date}</span>}
    {readTime && (
      <>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1"><Clock size={12} /> {readTime}</span>
      </>
    )}
  </div>
);

// Big card with image on top, category, title, meta, excerpt
export const FeatureCard = ({ post }) => (
  <article className="group">
    <Link to={`/article/${post.slug}`} className="block overflow-hidden">
      <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
        <SmartImage src={post.image} alt={post.title} />
      </div>
    </Link>
    <div className="pt-4">
      <CategoryBadge category={post.category} />
      <h2 className="mt-3 text-[22px] md:text-[26px] leading-tight font-bold text-gray-900 group-hover:text-[#8b2c3f] transition-colors">
        <Link to={`/article/${post.slug}`}>{post.title}</Link>
      </h2>
      <div className="mt-2">
        <MetaLine author={post.author} date={post.date} readTime={post.readTime} />
      </div>
      {post.excerpt && (
        <p className="mt-3 text-[14px] text-gray-600 leading-relaxed line-clamp-2">{post.excerpt}</p>
      )}
    </div>
  </article>
);

// Card with image + text side by side (for latest articles list)
export const HorizontalCard = ({ post }) => (
  <article className="group grid grid-cols-1 md:grid-cols-[280px_1fr] gap-5 border-b border-gray-100 pb-6">
    <Link to={`/article/${post.slug}`} className="relative block overflow-hidden aspect-[16/10] bg-gray-100">
      <SmartImage src={post.image} alt={post.title} />
    </Link>
    <div>
      <div className="flex items-center gap-3 text-[11px] uppercase font-bold text-[#8b2c3f] tracking-wider">
        <a href="#">{post.category}</a>
        {post.readTime && <span className="inline-flex items-center gap-1 text-gray-400 normal-case tracking-normal font-normal text-[12px]">
          <Clock size={12} /> {post.readTime}
        </span>}
      </div>
      <h3 className="mt-2 text-[18px] md:text-[20px] font-bold leading-snug text-gray-900 group-hover:text-[#8b2c3f] transition-colors">
        <Link to={`/article/${post.slug}`}>{post.title}</Link>
      </h3>
      {post.excerpt && <p className="mt-2 text-[13px] text-gray-600 leading-relaxed line-clamp-2">{post.excerpt}</p>}
      <div className="mt-2"><MetaLine author={post.author} date={post.date} /></div>
    </div>
  </article>
);

// Compact card (used in Trending Now and Health Fitness grid)
export const CompactCard = ({ post }) => (
  <article className="group">
    <Link to={`/article/${post.slug}`} className="relative block aspect-[16/10] overflow-hidden bg-gray-100">
      <SmartImage src={post.image} alt={post.title} />
    </Link>
    <div className="pt-3">
      <h4 className="text-[15px] font-bold leading-snug text-gray-900 group-hover:text-[#8b2c3f] transition-colors line-clamp-3">
        <Link to={`/article/${post.slug}`}>{post.title}</Link>
      </h4>
      <div className="mt-2"><MetaLine author={post.author} date={post.date} /></div>
    </div>
  </article>
);

// Sidebar item small
export const SidebarItem = ({ post }) => (
  <article className="group flex gap-3">
    <Link to={`/article/${post.slug}`} className="shrink-0 w-[80px] h-[80px] overflow-hidden bg-gray-100">
      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-500" loading="lazy" />
    </Link>
    <div className="min-w-0">
      {post.category && (
        <div className="text-[10px] uppercase font-bold text-[#8b2c3f] tracking-wider">{post.category}</div>
      )}
      <h5 className="mt-1 text-[13px] font-semibold leading-snug text-gray-900 group-hover:text-[#8b2c3f] transition-colors line-clamp-3">
        <Link to={`/article/${post.slug}`}>{post.title}</Link>
      </h5>
      {(post.author || post.date) && (
        <div className="mt-1 text-[11px] text-gray-500">
          {post.author && <>Par <span className="text-gray-700">{post.author}</span> | </>}{post.date}
        </div>
      )}
    </div>
  </article>
);
